import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';

import { filter, map, NEVER, Observable, of } from 'rxjs';

import { LanguageService } from '@fcom/ui-translate';
import { finShare } from '@fcom/rx';
import {
  BadgeVariant,
  ButtonMode,
  ButtonSize,
  ButtonTheme,
  IconPosition,
  TabLayoutType,
  TabTheme,
} from '@fcom/ui-components';
import { LoaderType } from '@fcom/common';
import { equals } from '@fcom/core/utils';
import { isShortHaulFlight } from '@fcom/booking/utils';

import {
  Category,
  FinnairBoundItem,
  FinnairCart,
  FinnairOrder,
  FinnairPassengerItem,
  FinnairServiceCatalogItemV3,
} from '../../api/models';
import { BoundWithServices, FinnairServiceItemWithUpsell, Upsell, UpsellCategoriesForBound } from '../../interfaces';
import { isAdult } from '../../utils';

@Component({
  selector: 'fin-dapi-included-products',
  templateUrl: './included-products.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DapiIncludedProductsComponent implements OnInit, OnChanges {
  readonly Category = Category;
  readonly IconPosition = IconPosition;
  readonly LoaderType = LoaderType;
  readonly ButtonMode = ButtonMode;
  readonly BadgeVariant = BadgeVariant;
  readonly TabTheme = TabTheme;
  readonly TabLayoutType = TabLayoutType;
  readonly ButtonTheme = ButtonTheme;
  readonly ButtonSize = ButtonSize;

  @Input() booking: FinnairCart | FinnairOrder;

  @Input() showTickets = false;

  @Input() showModifyButtons = false;

  @Input() showInformationModal = false;

  @Input() useCabinClass = false;

  @Input() titleTranslationKey;

  @Input() showAdditionalTitle = false;

  @Input() expandOpen = false;

  @Input() headerContentTranslationKey?: string;

  @Input() showUpsellCardForSeats = false;

  @Input() servicesToShow: FinnairServiceItemWithUpsell[];

  @Input() servicesReady$: Observable<boolean> = of(false);

  @Input() showAsPending = false;

  @Input() upsellInModal$: Observable<UpsellCategoriesForBound> = NEVER;

  @Input() headingClasses = 'font-heading-4';

  @Output()
  modifyClicked: EventEmitter<Category> = new EventEmitter<Category>();

  @Output()
  upsellClicked: EventEmitter<Category> = new EventEmitter<Category>();

  servicesForBounds: BoundWithServices[];

  headerContents$: Observable<string[]>;

  isShortHaulFlight: boolean;

  constructor(private languageService: LanguageService) {}

  ngOnInit(): void {
    this.updateServices();

    this.headerContents$ = this.headerContentTranslationKey
      ? this.languageService.translate(this.headerContentTranslationKey).pipe(
          map((content) => (Array.isArray(content) ? content : [content])),
          filter(Boolean),
          finShare()
        )
      : NEVER;

    this.isShortHaulFlight = this.isShortHaul(this.booking);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      !equals(changes['showUpsellCardForSeats']?.currentValue, changes['showUpsellCardForSeats']?.previousValue) ||
      !equals(changes['servicesToShow']?.currentValue, changes['servicesToShow']?.previousValue) ||
      !equals(changes['booking']?.currentValue, changes['booking']?.previousValue)
    ) {
      this.updateServices();
    }
  }

  private isShortHaul(booking: FinnairCart | FinnairOrder): boolean {
    const locations = Object.values(booking.locations);
    return isShortHaulFlight(locations[0], locations[1]);
  }

  private updateServices(): void {
    const servicesToShow = this.getServicesToShow();
    this.servicesForBounds = this.getServicesByBound(servicesToShow, this.booking.bounds);
  }

  private getServicesByBound(
    services: FinnairServiceItemWithUpsell[],
    allBounds: FinnairBoundItem[]
  ): BoundWithServices[] {
    return allBounds.map((bound) => {
      const servicesForBound: FinnairServiceItemWithUpsell[] = services
        .map((service) => ({
          ...service,
          bounds: service.bounds.filter(({ id }) => id === bound.id),
        }))
        .filter(({ bounds }) => bounds.length > 0);
      return {
        ...bound,
        services: this.showUpsellCardForSeats
          ? this.addSeatUpsell(
              servicesForBound,
              this.booking.passengers,
              this.servicesReady$,
              this.booking.serviceCatalog?.categories
            )
          : servicesForBound,
      };
    });
  }

  private getServicesToShow(): FinnairServiceItemWithUpsell[] {
    return this.addEmptySeat(this.servicesToShow, this.booking.bounds);
  }

  private addEmptySeat(
    services: FinnairServiceItemWithUpsell[],
    bounds: FinnairBoundItem[]
  ): FinnairServiceItemWithUpsell[] {
    if (!services.some(({ category }) => category === Category.SEAT) && this.showUpsellCardForSeats) {
      const [first, ...rest] = this.servicesToShow;
      const emptySeat: FinnairServiceItemWithUpsell = {
        category: Category.SEAT,
        bounds: bounds.map(({ id }) => ({ id, segments: [], totalPrice: null, quantity: 0 })),
        quantity: 0,
        title: null,
        totalPrice: { amount: '0', currencyCode: first?.totalPrice.currencyCode ?? 'EUR' },
      };
      return [first, emptySeat, ...rest].filter(Boolean);
    }

    return services;
  }

  private addSeatUpsell(
    services: FinnairServiceItemWithUpsell[],
    passengers: FinnairPassengerItem[],
    showUpsellButton$: Observable<boolean>,
    serviceCatalogCategories: FinnairServiceCatalogItemV3[]
  ): FinnairServiceItemWithUpsell[] {
    return services.map((service) => {
      if (service.category === Category.SEAT) {
        const availableService = (serviceCatalogCategories || [])?.find(
          ({ category: serviceCategory }) => serviceCategory === Category.SEAT
        );

        if (!availableService || (availableService && Object.keys(availableService?.lowestPrice || {}).length === 0)) {
          return service;
        }

        const includeUpSell = Object.entries(availableService?.lowestPrice).some(
          ([flight, paxServicePriceForFlight]) =>
            service.bounds.some((bound) => bound.id.includes(flight)) &&
            Object.keys(paxServicePriceForFlight).length > 0
        );

        if (includeUpSell) {
          const articleKey =
            passengers.filter(isAdult).length === 1
              ? 'MMB.fragments.upsell.seat.onePax.url'
              : 'MMB.fragments.upsell.seat.twoPax.url';

          const upsell: Upsell = {
            showButton$: showUpsellButton$,
            articleKey,
          };

          return {
            ...service,
            upsell,
          };
        }
      }

      return service;
    });
  }
}
